/**
 * Settings page — profile, passkeys, OpenAI configuration, theme, sign-out.
 */

import { useEffect, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import {
  Bell,
  CheckCircle2,
  Copy,
  Download,
  KeyRound,
  Lock,
  LogOut,
  Plug,
  Trash2,
} from "lucide-react";
import {
  useConfig,
  useCreateMcpToken,
  useCredentials,
  useMcpGrants,
  useRevokeMcpGrant,
  useDeleteCredential,
  useDeleteOpenAIKey,
  useLogout,
  useMcpTokens,
  useMe,
  useRenameCredential,
  useRevokeMcpToken,
  useSetOpenAIKey,
  useDeleteNtfyToken,
  useSetNtfyToken,
  useTestNotification,
  useTestOpenAIKey,
  useUpdateConfig,
  useVapidPublicKey,
} from "../api/hooks";
import {
  getPushSubscription,
  pushSupported,
  subscribeToPush,
  unsubscribeFromPush,
} from "../lib/push";
import { useToast } from "../components/toast";
import { useConfirm } from "../components/confirm";
import { useTheme } from "../lib/theme";
import { startRegistration } from "@simplewebauthn/browser";
import { api } from "../lib/api";
import { useQueryClient } from "@tanstack/react-query";

export const Route = createFileRoute("/settings")({
  component: SettingsPage,
});

function SettingsPage() {
  const meQ = useMe();
  const credsQ = useCredentials();
  const cfgQ = useConfig();
  const renameCred = useRenameCredential();
  const deleteCred = useDeleteCredential();
  const setKey = useSetOpenAIKey();
  const deleteKey = useDeleteOpenAIKey();
  const testKey = useTestOpenAIKey();
  const updateConfig = useUpdateConfig();
  const logout = useLogout();
  const toast = useToast();
  const confirm = useConfirm();
  const theme = useTheme();
  const [apiKey, setApiKey] = useState("");
  const qc = useQueryClient();

  async function addPasskey() {
    if (!meQ.data) return;
    const start = await api.post<{
      handle?: string;
      options?: unknown;
      suppressed?: boolean;
    }>("/auth/register/start", { username: meQ.data.username });
    if (start.suppressed) {
      toast.push("Passkeys disabled in this environment", "info");
      return;
    }
    if (!start.handle || !start.options) throw new Error("malformed");
    try {
      const response = await startRegistration({ optionsJSON: start.options as never });
      await api.post("/auth/register/finish", { handle: start.handle, response });
      qc.invalidateQueries({ queryKey: ["credentials"] });
      toast.push("Passkey added", "success");
    } catch (err) {
      toast.push((err as Error).message, "error");
    }
  }

  return (
    <div className="max-w-2xl mx-auto px-3 sm:px-6 py-6 flex flex-col gap-6">
      <h1 className="text-base font-semibold">Settings</h1>

      <section className="rounded-lg border p-4" style={{ borderColor: "var(--border)" }}>
        <h2 className="font-semibold mb-2 text-sm">Profile</h2>
        <div className="text-sm" style={{ color: "var(--fg)" }}>
          Username: <code>{meQ.data?.username}</code>
        </div>
        <div className="text-sm" style={{ color: "var(--fg)" }}>
          Display name: {meQ.data?.display_name}
        </div>
      </section>

      <section className="rounded-lg border p-4" style={{ borderColor: "var(--border)" }}>
        <div className="flex items-center justify-between mb-2">
          <h2 className="font-semibold text-sm flex items-center gap-2">
            <Lock className="w-4 h-4" /> Passkeys
          </h2>
          <button
            type="button"
            onClick={addPasskey}
            className="text-xs px-2 py-1 rounded bg-amber-400 text-black font-medium"
          >
            Add passkey
          </button>
        </div>
        <ul className="flex flex-col gap-2">
          {(credsQ.data ?? []).map((c) => (
            <li
              key={c.id}
              className="flex items-center gap-2 p-2 rounded border"
              style={{ borderColor: "var(--border)" }}
            >
              <KeyRound className="w-4 h-4 shrink-0" />
              {/* Stack name over device/date on mobile so the row never
               * overflows the viewport; inline them side-by-side on ≥ sm. */}
              <div className="flex-1 min-w-0 flex flex-col sm:flex-row sm:items-center sm:gap-2">
                <input
                  defaultValue={c.name ?? "Passkey"}
                  onBlur={(e) => {
                    if (e.target.value && e.target.value !== c.name)
                      renameCred.mutate({ id: c.id, name: e.target.value });
                  }}
                  className="w-full sm:flex-1 min-w-0 bg-transparent border-none outline-none text-sm"
                />
                <span
                  className="text-xs sm:whitespace-nowrap sm:shrink-0"
                  style={{ color: "var(--fg-muted)" }}
                >
                  {c.device_type ?? "device"} · {new Date(c.created_at).toLocaleDateString()}
                </span>
              </div>
              <button
                type="button"
                disabled={(credsQ.data ?? []).length <= 1}
                onClick={async () => {
                  const ok = await confirm({
                    title: "Remove this passkey?",
                    message: "You won't be able to sign in with this device anymore.",
                    confirmLabel: "Remove",
                    danger: true,
                  });
                  if (ok)
                    deleteCred.mutate(c.id, {
                      onError: (err) => toast.push((err as Error).message, "error"),
                    });
                }}
                aria-label="Remove passkey"
                className="p-1 rounded hover:bg-black/5 dark:hover:bg-white/5 disabled:opacity-30"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </li>
          ))}
        </ul>
      </section>

      <section className="rounded-lg border p-4" style={{ borderColor: "var(--border)" }}>
        <h2 className="font-semibold text-sm mb-2">AI / OpenAI</h2>
        {cfgQ.data?.ai_enabled ? (
          <div className="flex flex-col gap-2 text-sm">
            <div className="inline-flex items-center gap-2 text-green-700 dark:text-green-400">
              <CheckCircle2 className="w-4 h-4" /> An OpenAI key is configured.
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() =>
                  testKey.mutate(undefined, {
                    onSuccess: (r) => toast.push(`OpenAI: ${r.reply}`, "success"),
                    onError: (err) => toast.push((err as Error).message, "error"),
                  })
                }
                disabled={testKey.isPending}
                className="px-3 py-1.5 rounded border text-xs font-medium"
                style={{ borderColor: "var(--border)" }}
              >
                Test
              </button>
              <button
                type="button"
                onClick={async () => {
                  const ok = await confirm({
                    title: "Remove the stored OpenAI key?",
                    message: "AI features will be disabled until you add a key again.",
                    confirmLabel: "Remove",
                    danger: true,
                  });
                  if (ok)
                    deleteKey.mutate(undefined, {
                      onSuccess: () => toast.push("API key removed", "info"),
                    });
                }}
                className="px-3 py-1.5 rounded border text-xs font-medium text-red-600"
                style={{ borderColor: "var(--border)" }}
              >
                Remove key
              </button>
            </div>
          </div>
        ) : (
          <form
            className="flex flex-col gap-2 text-sm"
            onSubmit={(e) => {
              e.preventDefault();
              if (!apiKey) return;
              setKey.mutate(apiKey, {
                onSuccess: () => {
                  toast.push("API key saved — AI features enabled", "success");
                  setApiKey("");
                },
                onError: (err) => toast.push((err as Error).message, "error"),
              });
            }}
          >
            <label className="flex flex-col gap-1">
              <span style={{ color: "var(--fg-muted)" }}>OpenAI API key</span>
              <input
                type="password"
                placeholder="sk-…"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                className="px-3 py-2 rounded border"
                style={{ borderColor: "var(--border)", background: "var(--bg)" }}
              />
            </label>
            <p className="text-xs" style={{ color: "var(--fg-muted)" }}>
              Stored encrypted in your account. Enables Simplify, Fix, and semantic search.
            </p>
            <button
              type="submit"
              disabled={!apiKey || setKey.isPending}
              className="self-start mt-1 px-3 py-1.5 rounded bg-amber-400 text-black text-xs font-medium"
            >
              Save key
            </button>
          </form>
        )}
        <div className="mt-3 flex flex-col gap-2 text-sm">
          <label className="flex items-center gap-2">
            <span className="w-28" style={{ color: "var(--fg-muted)" }}>
              Chat model
            </span>
            <input
              defaultValue={cfgQ.data?.ai_model}
              onBlur={(e) => {
                if (e.target.value && e.target.value !== cfgQ.data?.ai_model)
                  updateConfig.mutate({ ai_model: e.target.value });
              }}
              className="flex-1 px-2 py-1 rounded border text-sm"
              style={{ borderColor: "var(--border)", background: "var(--bg)" }}
            />
          </label>
          <label className="flex items-center gap-2">
            <span className="w-28" style={{ color: "var(--fg-muted)" }}>
              Embedding
            </span>
            <input
              defaultValue={cfgQ.data?.embedding_model}
              onBlur={(e) => {
                if (e.target.value && e.target.value !== cfgQ.data?.embedding_model)
                  updateConfig.mutate({ embedding_model: e.target.value });
              }}
              className="flex-1 px-2 py-1 rounded border text-sm"
              style={{ borderColor: "var(--border)", background: "var(--bg)" }}
            />
          </label>
        </div>
      </section>

      <NotificationsCard />

      <McpAccessCard />

      <section className="rounded-lg border p-4" style={{ borderColor: "var(--border)" }}>
        <h2 className="font-semibold text-sm mb-2 flex items-center gap-2">
          <Download className="w-4 h-4" /> Get your data out
        </h2>
        <p className="text-xs mb-3" style={{ color: "var(--fg-muted)" }}>
          Download a zip with every note (as markdown with frontmatter), every image, your folder
          list, your share-link metadata, and your basic profile. Your encrypted OpenAI key is not
          included.
        </p>
        {/*
          Plain anchor: the server streams a zip and we want the browser's
          native download UI. Using a mutation would force us to buffer the
          whole archive in memory before saving.
        */}
        <a
          href="/api/v1/export/all"
          download
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded border text-xs font-medium"
          style={{ borderColor: "var(--border)" }}
        >
          <Download className="w-3.5 h-3.5" />
          Export all data
        </a>
      </section>

      <section className="rounded-lg border p-4" style={{ borderColor: "var(--border)" }}>
        <h2 className="font-semibold text-sm mb-2">Appearance</h2>
        <div className="flex items-center gap-2 text-sm">
          {(["system", "light", "dark"] as const).map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => theme.setTheme(t)}
              className={`px-3 py-1.5 rounded border text-xs font-medium ${
                theme.theme === t ? "bg-amber-400 text-black border-amber-400" : ""
              }`}
              style={{ borderColor: "var(--border)" }}
            >
              {t}
            </button>
          ))}
        </div>
      </section>

      <button
        type="button"
        onClick={() =>
          logout.mutate(undefined, {
            onSuccess: () => {
              location.reload();
            },
          })
        }
        className="self-start inline-flex items-center gap-2 px-3 py-2 rounded border text-sm"
        style={{ borderColor: "var(--border)" }}
      >
        <LogOut className="w-4 h-4" />
        Sign out
      </button>
    </div>
  );
}

/**
 * Notifications card — manage Web Push (per-device browser/PWA notifications)
 * and an optional ntfy.sh topic. Reminders are delivered to whichever channels
 * are enabled here by a server-side cron (worker/lib/reminders.ts).
 */
function NotificationsCard() {
  const cfgQ = useConfig();
  const vapidQ = useVapidPublicKey();
  const updateConfig = useUpdateConfig();
  const setNtfyToken = useSetNtfyToken();
  const deleteNtfyToken = useDeleteNtfyToken();
  const testNotification = useTestNotification();
  const toast = useToast();

  const [pushOn, setPushOn] = useState<boolean | null>(null); // null = still checking
  const [busy, setBusy] = useState(false);
  const [ntfy, setNtfy] = useState("");
  const [ntfyToken, setNtfyTokenInput] = useState("");
  const tokenSet = cfgQ.data?.ntfy_token_set ?? false;

  useEffect(() => {
    setNtfy(cfgQ.data?.ntfy_url ?? "");
  }, [cfgQ.data?.ntfy_url]);

  useEffect(() => {
    if (!pushSupported()) {
      setPushOn(false);
      return;
    }
    getPushSubscription().then((sub) => setPushOn(!!sub));
  }, []);

  const supported = pushSupported();
  const vapidReady = !!vapidQ.data;

  async function togglePush() {
    setBusy(true);
    try {
      if (pushOn) {
        await unsubscribeFromPush();
        setPushOn(false);
        toast.push("Browser notifications turned off", "info");
      } else {
        if (!vapidQ.data) throw new Error("Push isn't configured on the server.");
        await subscribeToPush(vapidQ.data);
        setPushOn(true);
        toast.push("Browser notifications enabled", "success");
      }
    } catch (err) {
      toast.push((err as Error).message, "error");
    } finally {
      setBusy(false);
    }
  }

  function saveNtfy() {
    const value = ntfy.trim();
    if (value === (cfgQ.data?.ntfy_url ?? "")) return;
    updateConfig.mutate(
      { ntfy_url: value || null },
      {
        onSuccess: () => toast.push(value ? "ntfy URL saved" : "ntfy URL cleared", "success"),
        onError: (err) => toast.push((err as Error).message, "error"),
      }
    );
  }

  return (
    <section className="rounded-lg border p-4" style={{ borderColor: "var(--border)" }}>
      <h2 className="font-semibold text-sm mb-2 flex items-center gap-2">
        <Bell className="w-4 h-4" /> Notifications
      </h2>
      <p className="text-xs mb-3" style={{ color: "var(--fg-muted)" }}>
        Get reminded when a note's reminder time arrives. Choose either or both channels.
      </p>

      {/* Web Push */}
      <div className="flex flex-col gap-1 text-sm">
        <div className="flex items-center justify-between gap-2">
          <span className="font-medium">Browser / PWA notifications</span>
          <button
            type="button"
            disabled={busy || pushOn === null || !supported || (!pushOn && !vapidReady)}
            onClick={togglePush}
            className="px-3 py-1.5 rounded border text-xs font-medium disabled:opacity-40"
            style={{ borderColor: "var(--border)" }}
          >
            {pushOn ? "Turn off" : "Enable"}
          </button>
        </div>
        <p className="text-xs" style={{ color: "var(--fg-muted)" }}>
          {!supported
            ? "This browser doesn't support push notifications. On iPhone/iPad, add keep to your Home Screen first."
            : !vapidReady
              ? "Web Push isn't configured on the server yet."
              : pushOn
                ? "This device will receive reminder notifications."
                : "Enable to receive reminders on this device, even when keep is closed."}
        </p>
      </div>

      {/* ntfy.sh */}
      <div className="mt-4 flex flex-col gap-1 text-sm">
        <span className="font-medium">ntfy.sh topic URL</span>
        <div className="flex items-center gap-2">
          <input
            type="url"
            inputMode="url"
            placeholder="https://ntfy.sh/your-topic"
            value={ntfy}
            onChange={(e) => setNtfy(e.target.value)}
            onBlur={saveNtfy}
            className="flex-1 px-3 py-2 rounded border min-w-0"
            style={{ borderColor: "var(--border)", background: "var(--bg)" }}
          />
          <button
            type="button"
            onClick={saveNtfy}
            disabled={updateConfig.isPending}
            className="px-3 py-2 rounded bg-amber-400 text-black text-xs font-medium shrink-0"
          >
            Save
          </button>
        </div>
        <p className="text-xs" style={{ color: "var(--fg-muted)" }}>
          Pick a hard-to-guess topic, then subscribe to it in the{" "}
          <a href="https://ntfy.sh/app" target="_blank" rel="noreferrer" className="underline">
            ntfy app
          </a>
          . Reminders for your notes will be pushed there.
        </p>
      </div>

      {/* ntfy access token (optional, for protected topics) */}
      <div className="mt-3 flex flex-col gap-1 text-sm">
        <span className="font-medium">
          ntfy access token{" "}
          <span className="font-normal" style={{ color: "var(--fg-muted)" }}>
            (optional)
          </span>
        </span>
        <div className="flex items-center gap-2">
          <input
            type="password"
            placeholder={tokenSet ? "•••••••••• (configured)" : "tk_… for a protected topic"}
            value={ntfyToken}
            onChange={(e) => setNtfyTokenInput(e.target.value)}
            autoComplete="off"
            className="flex-1 px-3 py-2 rounded border min-w-0"
            style={{ borderColor: "var(--border)", background: "var(--bg)" }}
          />
          <button
            type="button"
            disabled={!ntfyToken.trim() || setNtfyToken.isPending}
            onClick={() =>
              setNtfyToken.mutate(ntfyToken.trim(), {
                onSuccess: () => {
                  setNtfyTokenInput("");
                  toast.push("ntfy token saved", "success");
                },
                onError: (err) => toast.push((err as Error).message, "error"),
              })
            }
            className="px-3 py-2 rounded bg-amber-400 text-black text-xs font-medium shrink-0"
          >
            Save
          </button>
          {tokenSet && (
            <button
              type="button"
              disabled={deleteNtfyToken.isPending}
              onClick={() =>
                deleteNtfyToken.mutate(undefined, {
                  onSuccess: () => toast.push("ntfy token removed", "info"),
                })
              }
              className="px-3 py-2 rounded border text-xs font-medium text-red-600 shrink-0"
              style={{ borderColor: "var(--border)" }}
            >
              Clear
            </button>
          )}
        </div>
        <p className="text-xs" style={{ color: "var(--fg-muted)" }}>
          Only needed for protected topics — create an access token in ntfy and paste it here.
          Stored write-only; sent as a Bearer token.
        </p>
      </div>

      {/* Test */}
      <div className="mt-4">
        <button
          type="button"
          disabled={testNotification.isPending}
          onClick={() =>
            testNotification.mutate(undefined, {
              onSuccess: (r) =>
                toast.push(
                  r.ok
                    ? `Test sent (ntfy: ${r.ntfy ? "yes" : "no"}, push devices: ${r.push})`
                    : "No channels configured yet.",
                  r.ok ? "success" : "info"
                ),
              onError: (err) => toast.push((err as Error).message, "error"),
            })
          }
          className="px-3 py-1.5 rounded border text-xs font-medium"
          style={{ borderColor: "var(--border)" }}
        >
          Send test notification
        </button>
      </div>
    </section>
  );
}

/**
 * MCP access — connect Claude to keep over the Model Context Protocol. Two
 * paths: a static API token (Claude Code / CLI) created here, or the OAuth
 * connector URL (claude.ai "Add custom connector"). The plaintext token is
 * shown exactly once.
 */
function McpAccessCard() {
  const tokensQ = useMcpTokens();
  const createToken = useCreateMcpToken();
  const revokeToken = useRevokeMcpToken();
  const grantsQ = useMcpGrants();
  const revokeGrant = useRevokeMcpGrant();
  const grants = grantsQ.data ?? [];
  const toast = useToast();
  const confirm = useConfirm();
  const [name, setName] = useState("");
  const [fresh, setFresh] = useState<string | null>(null);

  const mcpUrl = `${location.origin}/mcp`;
  const active = (tokensQ.data ?? []).filter((t) => !t.revoked_at);

  function copy(text: string, label: string) {
    navigator.clipboard?.writeText(text).then(
      () => toast.push(`${label} copied`, "success"),
      () => toast.push("Copy failed", "error")
    );
  }

  return (
    <section className="rounded-lg border p-4" style={{ borderColor: "var(--border)" }}>
      <h2 className="font-semibold text-sm mb-2 flex items-center gap-2">
        <Plug className="w-4 h-4" /> MCP access
      </h2>
      <p className="text-xs mb-3" style={{ color: "var(--fg-muted)" }}>
        Let Claude read and write your notes over MCP. Use the connector URL on claude.ai, or create
        a token for Claude Code / CLI clients.
      </p>

      {/* Connector URL (OAuth) */}
      <div className="flex flex-col gap-1 text-sm mb-4">
        <span className="font-medium">claude.ai connector URL</span>
        <div className="flex items-center gap-2">
          <code
            className="flex-1 min-w-0 truncate px-2 py-1.5 rounded border text-xs"
            style={{ borderColor: "var(--border)", background: "var(--bg)" }}
          >
            {mcpUrl}
          </code>
          <button
            type="button"
            onClick={() => copy(mcpUrl, "URL")}
            aria-label="Copy connector URL"
            className="p-2 rounded border shrink-0"
            style={{ borderColor: "var(--border)" }}
          >
            <Copy className="w-4 h-4" />
          </button>
        </div>
        <span className="text-xs" style={{ color: "var(--fg-muted)" }}>
          In claude.ai: Settings → Connectors → Add custom connector. You'll approve access with
          your passkey.
        </span>
      </div>

      {/* Freshly-created token (shown once) */}
      {fresh && (
        <div
          className="mb-4 rounded border p-3 text-sm"
          style={{ borderColor: "var(--border)", background: "var(--bg)" }}
        >
          <div className="font-medium mb-1">New token — copy it now, it won't be shown again:</div>
          <div className="flex items-center gap-2">
            <code className="flex-1 min-w-0 truncate text-xs">{fresh}</code>
            <button
              type="button"
              onClick={() => copy(fresh, "Token")}
              className="p-2 rounded border shrink-0"
              style={{ borderColor: "var(--border)" }}
              aria-label="Copy token"
            >
              <Copy className="w-4 h-4" />
            </button>
          </div>
          <div className="mt-2 text-xs" style={{ color: "var(--fg-muted)" }}>
            Add to Claude Code:
          </div>
          <code
            className="block mt-1 overflow-x-auto rounded p-2 text-xs"
            style={{ background: "var(--bg-elevated)" }}
          >
            claude mcp add --transport http keep {mcpUrl} --header &quot;Authorization: Bearer{" "}
            {fresh}&quot;
          </code>
        </div>
      )}

      {/* Create */}
      <form
        className="flex items-center gap-2 mb-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (!name.trim()) return;
          createToken.mutate(name.trim(), {
            onSuccess: (r) => {
              setFresh(r.token);
              setName("");
            },
            onError: (err) => toast.push((err as Error).message, "error"),
          });
        }}
      >
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Token name (e.g. Laptop CLI)"
          className="flex-1 min-w-0 px-3 py-2 rounded border text-sm"
          style={{ borderColor: "var(--border)", background: "var(--bg)" }}
        />
        <button
          type="submit"
          disabled={!name.trim() || createToken.isPending}
          className="px-3 py-2 rounded bg-amber-400 text-black text-xs font-medium shrink-0"
        >
          Create token
        </button>
      </form>

      {/* List */}
      {active.length > 0 && (
        <ul className="flex flex-col gap-2">
          {active.map((t) => (
            <li
              key={t.id}
              className="flex items-center gap-2 p-2 rounded border text-sm"
              style={{ borderColor: "var(--border)" }}
            >
              <KeyRound className="w-4 h-4 shrink-0" />
              <div className="flex-1 min-w-0 flex flex-col sm:flex-row sm:items-center sm:gap-2">
                <span className="truncate">{t.name}</span>
                <span className="text-xs font-mono" style={{ color: "var(--fg-muted)" }}>
                  {t.token_prefix}…
                </span>
              </div>
              <button
                type="button"
                onClick={async () => {
                  const ok = await confirm({
                    title: `Revoke token "${t.name}"?`,
                    message: "Anything using this token will immediately lose access.",
                    confirmLabel: "Revoke",
                    danger: true,
                  });
                  if (ok)
                    revokeToken.mutate(t.id, {
                      onError: (err) => toast.push((err as Error).message, "error"),
                    });
                }}
                aria-label="Revoke token"
                className="p-1 rounded hover:bg-black/5 dark:hover:bg-white/5 shrink-0"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </li>
          ))}
        </ul>
      )}

      {/* OAuth connections (claude.ai connector grants) */}
      {grants.length > 0 && (
        <div className="mt-4">
          <span className="font-medium text-sm">Connected apps (OAuth)</span>
          <ul className="mt-2 flex flex-col gap-2">
            {grants.map((g) => (
              <li
                key={g.client_id}
                className="flex items-center gap-2 p-2 rounded border text-sm"
                style={{ borderColor: "var(--border)" }}
              >
                <Plug className="w-4 h-4 shrink-0" />
                <div className="flex-1 min-w-0 flex flex-col sm:flex-row sm:items-center sm:gap-2">
                  <span className="truncate">{g.client_name}</span>
                  <span className="text-xs" style={{ color: "var(--fg-muted)" }}>
                    {g.last_used_at
                      ? `last used ${new Date(g.last_used_at * 1000).toLocaleDateString()}`
                      : "never used"}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={async () => {
                    const ok = await confirm({
                      title: `Disconnect "${g.client_name}"?`,
                      message:
                        "It will need to re-authorize before it can access your notes again.",
                      confirmLabel: "Disconnect",
                      danger: true,
                    });
                    if (ok)
                      revokeGrant.mutate(g.client_id, {
                        onError: (err) => toast.push((err as Error).message, "error"),
                      });
                  }}
                  aria-label="Disconnect app"
                  className="p-1 rounded hover:bg-black/5 dark:hover:bg-white/5 shrink-0"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}
